import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import { injectIntl } from 'react-intl';
import messages from './messagesAddOrder';
import inlineStyles from './styles';
import './stylesAddOrder.css';
import ShoppingSearch from '../../../Shopping/ShoppingSearch/ShoppingSearch';
import ShoppingResults from '../../../Shopping/ShoppingResults/ShoppingResults';
import CreateBooking from '../../../Shopping/CreateBooking/CreateBooking';
import { fetchBookingDetails } from '../../ManageBooking/actionsManageBooking';
import { clearSearchResults } from '../../../Shopping/ShoppingResults/actionsShoppingResults';
import { clearFields } from '../../../Shopping/ShoppingSearch/actionsShoppingSearch';
import {
    TRIP_TYPE_RETURN,
    TRIP_TYPE_SINGLE,
} from '../../../Shopping/util';

const AddOrderDialog = ({
    open,
    orderAccount,
    results,
    faresSelected,
    tripType,
    queryItems,
    passengers,
    classes,
    intl: { formatMessage },
    openReturn,
    onCloseAddOrderDialog,
    onClearSearchResults,
    onClearFields,
    onOrderAdded,
}) => {
    const [isProcessing, setIsProcessing] = useState(false);

    const createCompRef = React.useRef();
    const searchCompRef = React.useRef();

    const clearAddOrder = () => {
        onClearSearchResults();
        onClearFields();
    };

    useEffect(() => {
        clearAddOrder();

        return () => {
            clearAddOrder();
        };
    }, []);

    const handleClose = () => {
        clearAddOrder();
        setIsProcessing(false);
        onCloseAddOrderDialog();
    };

    const handleAddOrder = () => {
        handleClose();
        onOrderAdded(queryItems);
    };

    const handleStatusChange = (status) => {
        setIsProcessing(status);
    };

    const showResults = results && results.size > 0;
    const showAddOrder = (faresSelected.size === 1 && (tripType === TRIP_TYPE_SINGLE || openReturn))
        || (faresSelected.size === 2 && tripType === TRIP_TYPE_RETURN);

    let componentMainAction = null;
    let componentActionLabel = '';
    let componentActionId;
    let component = null;
    let componentActionInProcess = false;

    // To get References to components we need to use getWrappedInstance twice because the components are wrapped 2 times,
    // once by connect and second by react-intl
    if (!showResults) {
        component = (
            <ShoppingSearch
                innerRef={searchCompRef}
                addOrderMode
                orderPassengers={passengers}
                orderAccount={orderAccount}
            />
        );

        componentMainAction = () => { searchCompRef.current.handleSubmitSearch(); };
        componentActionLabel = formatMessage(messages.btnSearch);
        componentActionId = 'srtAddOrderSearch';
        // For Search component we can use props to determine component state
        componentActionInProcess = searchCompRef.current
            && (searchCompRef.current.props.query.get('isFetchingUserState') || searchCompRef.current.props.isSearchInitiated);
    } else {
        component = (
            <ShoppingResults
                addOrderMode
            />
        );
    }

    if (showAddOrder) {
        // since component status in Create Booking is determined by state
        // we need to make a callback on each component status change
        component = (
            <CreateBooking
                ref={createCompRef}
                addOrderMode
                orderPassengers={passengers}
                queryItems={queryItems}
                onAddOrder={handleAddOrder}
                onStatusChange={handleStatusChange}
            />
        );

        componentMainAction = () => {
            createCompRef.current.handleAddOrder();
        };
        componentActionLabel = formatMessage(messages.btnAddOrder);
        componentActionId = 'srtAddOrderAddOrder';
    }

    const disabled = componentActionInProcess || isProcessing;

    const columnOffset = (componentMainAction)
        ? 'offset-sm-4 offset-md-6 offset-xl-8' : 'offset-sm-8 offset-md-9 offset-xl-10';

    const actions = (
        <div className="row" style={inlineStyles.dialogActionsRoot}>
            <div
                className={`col-12 col-sm-4 col-md-3 col-xl-2 ${columnOffset}`}
                style={inlineStyles.buttonFix}
            >
                <Button
                    variant="contained"
                    id="srtAddOrderClose"
                    onClick={handleClose}
                    fullWidth
                    disabled={disabled}
                >
                    {formatMessage(messages.btnClose)}
                </Button>
            </div>
            {
                !componentMainAction
                || (
                    <div
                        className="col-12 col-sm-4 col-md-3 col-xl-2"
                        style={inlineStyles.buttonFix}
                    >
                        <Button
                            variant="contained"
                            id={componentActionId}
                            onClick={componentMainAction}
                            fullWidth
                            color="primary"
                            disabled={disabled}
                        >
                            {componentActionLabel}
                        </Button>
                    </div>
                )
            }
        </div>
    );

    return (
        <Dialog
            open={open}
            classes={{ paper: classes.dialogPaper }}
            onClose={handleClose}
            disableEnforceFocus
            maxWidth={false}
        >
            <DialogTitle>{formatMessage(messages.lblTitle)}</DialogTitle>
            <DialogContent>
                <div className="container-fluid" styleName="srtAddOrderContainer">
                    {component}
                </div>
            </DialogContent>
            <DialogActions>
                {actions}
            </DialogActions>
        </Dialog>
    );
};

AddOrderDialog.propTypes = {
    intl: PropTypes.object,
    open: PropTypes.bool,
    queryItems: PropTypes.object,
    onCloseAddOrderDialog: PropTypes.func.isRequired,
    onOrderAdded: PropTypes.func,
    orderAccount: PropTypes.object,
    results: PropTypes.object,
    faresSelected: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object,
    ]),
    tripType: PropTypes.number,
    onClearSearchResults: PropTypes.func,
    onClearFields: PropTypes.func,
    passengers: PropTypes.array,
    classes: PropTypes.object.isRequired,
    openReturn: PropTypes.bool,
};

const mapStateToProps = (state) => ({
    results: state.getIn(['shopping', 'results', 'results']),
    faresSelected: state.getIn(['shopping', 'results', 'faresSelected']),
    tripType: state.getIn(['shopping', 'query', 'tripType']),
    openReturn: state.getIn(['shopping', 'query', 'openReturn']),
});

const mapDispatchToProps = (dispatch) => ({
    onOrderAdded: bindActionCreators(fetchBookingDetails, dispatch),
    onClearSearchResults: bindActionCreators(clearSearchResults, dispatch),
    onClearFields: bindActionCreators(clearFields, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
    injectIntl(withStyles(inlineStyles)(AddOrderDialog)),
);
